import { GroupsOutlined, RuleOutlined } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import type { GridRowSelectionModel, GridValidRowModel } from "@mui/x-data-grid";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import MetaChip from "../../../../../components/UIToolkit/Badges/MetaChip";
import { EmptyState } from "../../../../../components/UIToolkit/molecules/EmptyState";
import { InlineHint } from "../../../../../components/UIToolkit/molecules/InlineHint";
import { SettingsSection } from "../../../../../components/UIToolkit/molecules/SettingsSection";
import SystemButton from "../../../../../components/UIToolkit/SystemButton/SystemButton";
import { SystemDataGrid } from "../../../../../components/UIToolkit/Tables/SystemDataGrid";
import type { SystemGridColDef } from "../../../../../components/UIToolkit/Tables/SystemDataGrid/types";
import { useAnalytics } from "../../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../../contexts/HospitalDataProvider";
import { useJobs } from "../../../../../hooks/useJobs";
import { FONT_SIZE } from "../../../../../theme/tokens";
import type { CertPrimaryJobChangePreviewRow } from "../../../../../utils/models";
import { useCertPrimaryJobChangePreviewFetcher } from "../hrisMapping/hrisMappingHooks";
import { JobMappingChangeConfirmDialog } from "../hrisMapping/JobMappingChangeConfirmDialog";
import {
	CLIPBOARD_DISPOSITION_LABEL_KEY,
	CLIPBOARD_DISPOSITION_TONE,
	CLIPBOARD_PROPOSAL_REASON_KEY,
	CLIPBOARD_STATUS_MESSAGE_KEY,
} from "./clipboardOnboardingLabels";
import type {
	ClipboardCertOverrideSelection,
	ClipboardIgnoreSelection,
	ClipboardRoleMappingProposalResponse,
	ClipboardRoleMappingRow,
	ClipboardSubRoleSelection,
} from "./clipboardOnboardingTypes";

/**
 * The writes the operator opted into, already in payload shape (minus the facility id, which the wizard
 * owns). Shaped HERE rather than in the wizard because this is where `isWritable` has already proven the
 * target job and sub-role are present — so the apply body can never carry a blank the server rejects.
 */
export type ClipboardRoleMappingSelection = {
	subRoles: ClipboardSubRoleSelection[];
	certOverrides: ClipboardCertOverrideSelection[];
	ignores: ClipboardIgnoreSelection[];
};

type Props = {
	proposal: ClipboardRoleMappingProposalResponse | undefined;
	isLoading: boolean;
	/** Already-translated failure sentence when the proposal request itself failed, or null. */
	errorMessage: string | null;
	/**
	 * Offered only when the failure is retryable — Clipboard being unreachable (502) wrote nothing, so
	 * another attempt is the right affordance. A settled rejection (facility not managed) gets no retry.
	 */
	onRetry?: () => void;
	onSelectionChange: (selection: ClipboardRoleMappingSelection) => void;
};

/** Grids sized to a handful of rows, then scroll — a modal cannot give a 20-row grid its full height. */
const GRID_CONTAINED_HEIGHT = { minHeight: 140, maxHeight: 224 } as const;

const emptySelection = (): GridRowSelectionModel => ({ type: "include", ids: new Set() });

/**
 * `SystemDataGrid` types row params as `T | DetailRow<T>` because expansion can inject detail rows.
 * Neither grid here enables expansion, so a detail row is unreachable — but narrow rather than cast, so
 * turning expansion on later cannot silently make `isWritable` read fields off a detail row.
 */
const isProposalRow = (row: GridValidRowModel): row is ClipboardRoleMappingRow => typeof row.roleString === "string" && typeof row.proposal === "string";

/** Test ids have to be stable and kebab-safe; a raw worker type can be `RN/LPN` or carry spaces. */
const roleSlug = (row: ClipboardRoleMappingRow) =>
	row.normalized
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-|-$/g, "");

/**
 * MUI reports the header select-all as an `exclude` model — "everything except these ids" — which cannot
 * express "everything the operator is ALLOWED to opt into". Reconstructing it naively hands ONE click
 * every `ignore` proposal, which is precisely the choice the design requires to be deliberate (an ignore
 * writes an `hris_ignored_value` row and permanently stops those shifts arriving).
 *
 * So an exclude model is collapsed to a concrete `include` set at the boundary: state is only ever
 * `include`, select-all can only reach rows that are writable AND not an ignore, and the invariant holds
 * for both selection modes instead of only for the per-row checkbox.
 */
const normalizeSelection = (
	model: GridRowSelectionModel,
	rows: ClipboardRoleMappingRow[],
	canSelectAll: (row: ClipboardRoleMappingRow) => boolean,
): GridRowSelectionModel => {
	if (model.type === "include") return model;
	return {
		type: "include",
		ids: new Set(rows.filter((row) => canSelectAll(row) && !model.ids.has(row.roleString)).map((row) => row.roleString)),
	};
};

/**
 * Step 4 — turn the facility's contracted worker types into a role catalog.
 *
 * Split into two grids because the two tables have very different blast radii, which a single grid
 * cannot express: `job_department_sub_role` rows are scoped to Clipboard and only ever affect agency
 * shifts, while `department_certification_job` overrides are **shared with HRIS** and re-job existing
 * payroll employees. So the second grid carries the employee-impact column, and a row that would move
 * someone's job starts unchecked — you opt into it through the same preview dialog the HRIS Roles grid
 * uses, never as a side effect of an agency wizard.
 */
export const ClipboardRoleMappingStep: FC<Props> = ({ proposal, isLoading, errorMessage, onRetry, onSelectionChange }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const { trackError } = useAnalytics();
	const jobs = useJobs();
	const fetchPrimaryJobPreview = useCertPrimaryJobChangePreviewFetcher(chosenDepartment.department_id);

	const rows = useMemo(() => proposal?.rows ?? [], [proposal]);
	const jobLabel = useCallback((jobId: string | null) => (jobId ? (jobs.find((job) => job.id === jobId)?.displayName ?? jobId) : ""), [jobs]);

	// The Clipboard-scoped grid: sub-role rows, ignores, and rows that need nothing. The cert-override
	// grid is separate because only it can reach payroll employees.
	const catalogRows = useMemo(() => rows.filter((row) => row.proposal !== "cert_override"), [rows]);
	const certRows = useMemo(() => rows.filter((row) => row.proposal === "cert_override"), [rows]);

	const [catalogSelection, setCatalogSelection] = useState<GridRowSelectionModel>(emptySelection);
	const [certSelection, setCertSelection] = useState<GridRowSelectionModel>(emptySelection);
	const [reviewing, setReviewing] = useState<{ row: ClipboardRoleMappingRow; previewRows: CertPrimaryJobChangePreviewRow[] } | null>(null);
	const [reviewError, setReviewError] = useState<string | null>(null);

	// A proposal can only be opted into when it carries everything the apply needs. Both writes are keyed
	// on a job AND a sub-role (`department_certification_job.role` IS the lvn → LPN redirect and cannot be
	// derived from the job), so a row missing either is shown but not selectable rather than being sent
	// with a blank the server would reject.
	const isWritable = useCallback(
		(row: ClipboardRoleMappingRow) =>
			row.proposal === "ignore" || ((row.proposal === "sub_role" || row.proposal === "cert_override") && !!row.targetJobId && !!row.targetSubRole),
		[],
	);

	// What the HEADER checkbox may reach. Narrower than `isWritable` on purpose: an ignore proposal is
	// only ever selected by clicking its own row.
	const canSelectAll = useCallback((row: ClipboardRoleMappingRow) => isWritable(row) && row.proposal !== "ignore", [isWritable]);

	const handleCatalogSelectionChange = useCallback(
		(model: GridRowSelectionModel) => setCatalogSelection(normalizeSelection(model, catalogRows, canSelectAll)),
		[catalogRows, canSelectAll],
	);
	const handleCertSelectionChange = useCallback(
		(model: GridRowSelectionModel) => setCertSelection(normalizeSelection(model, certRows, canSelectAll)),
		[certRows, canSelectAll],
	);

	// Seed the selection from the proposal: everything the classifier is confident about is pre-checked,
	// EXCEPT the two kinds of choice that must be deliberate — ignoring a worker type (its shifts stop
	// arriving) and an override that would move real employees' jobs.
	//
	// Keyed on the CONTENT of the proposal, not the query's object identity: a background refetch that
	// returns the same rows must not silently undo the operator's checkboxes mid-review.
	const seededSignatureRef = useRef<string | null>(null);
	useEffect(() => {
		const signature = rows.map((row) => `${row.roleString}:${row.proposal}:${row.targetJobId}:${row.affectedEmployeeCount}`).join("|");
		if (seededSignatureRef.current === signature) return;
		seededSignatureRef.current = signature;
		setCatalogSelection({
			type: "include",
			ids: new Set(rows.filter((row) => row.proposal === "sub_role" && isWritable(row)).map((row) => row.roleString)),
		});
		setCertSelection({
			type: "include",
			ids: new Set(rows.filter((row) => row.proposal === "cert_override" && row.affectedEmployeeCount === 0 && isWritable(row)).map((row) => row.roleString)),
		});
	}, [rows, isWritable]);

	// Selected == in the include set AND still writable. `isWritable` is applied on the way OUT as well as
	// on the checkbox, so a refetch that turns a row unwritable can't leave a stale id in the payload.
	const selectedCatalogRows = useMemo(
		() => catalogRows.filter((row) => isWritable(row) && catalogSelection.ids.has(row.roleString)),
		[catalogRows, catalogSelection, isWritable],
	);
	const selectedCertRows = useMemo(
		() => certRows.filter((row) => isWritable(row) && certSelection.ids.has(row.roleString)),
		[certRows, certSelection, isWritable],
	);

	// Only rows whose target job AND sub-role are present survive into the payload, so the apply body has
	// no fallbacks in it — a row missing either is dropped here rather than sent as an empty string.
	useEffect(() => {
		onSelectionChange({
			subRoles: selectedCatalogRows.flatMap((row) =>
				row.proposal === "sub_role" && row.targetJobId && row.targetSubRole
					? [{ roleString: row.roleString, normalized: row.normalized, jobId: row.targetJobId, subRole: row.targetSubRole, isDefault: row.isDefault }]
					: [],
			),
			ignores: selectedCatalogRows.filter((row) => row.proposal === "ignore").map((row) => ({ roleString: row.roleString, normalized: row.normalized })),
			certOverrides: selectedCertRows.flatMap((row) =>
				row.targetJobId && row.targetSubRole
					? [{ roleString: row.roleString, normalized: row.normalized, jobId: row.targetJobId, subRole: row.targetSubRole }]
					: [],
			),
		});
	}, [selectedCatalogRows, selectedCertRows, onSelectionChange]);

	const proposalText = useCallback(
		(row: ClipboardRoleMappingRow) => {
			switch (row.proposal) {
				case "sub_role":
					return row.isDefault
						? t("department.config.clipboard.wizard.roles.proposal.subRoleDefault", { subRole: row.targetSubRole, job: jobLabel(row.targetJobId) })
						: t("department.config.clipboard.wizard.roles.proposal.subRole", { subRole: row.targetSubRole, job: jobLabel(row.targetJobId) });
				case "cert_override":
					return t("department.config.clipboard.wizard.roles.proposal.certOverride", { job: jobLabel(row.targetJobId) });
				case "ignore":
					return t("department.config.clipboard.wizard.roles.proposal.ignore");
				default:
					return t("department.config.clipboard.wizard.roles.proposal.none");
			}
		},
		[t, jobLabel],
	);

	const openReview = useCallback(
		async (row: ClipboardRoleMappingRow) => {
			if (!row.targetJobId) return;
			setReviewError(null);
			try {
				const preview = await fetchPrimaryJobPreview(row.roleString, row.targetJobId);
				setReviewing({ row, previewRows: preview.rows });
			} catch (error) {
				// The count already came from the proposal, so a failed preview must not silently look like
				// "no impact" — say the review is unavailable and leave the row unchecked.
				trackError("clipboard_onboarding_job_change_preview_error", {
					department_id: chosenDepartment.department_id,
					error_message: error instanceof Error ? error.message : String(error),
				});
				setReviewError(t("department.config.clipboard.wizard.roles.impact.previewFailed"));
			}
		},
		[fetchPrimaryJobPreview, t, trackError, chosenDepartment.department_id],
	);

	const sharedColumns = useMemo<SystemGridColDef<ClipboardRoleMappingRow>[]>(
		() => [
			{
				field: "roleString",
				headerName: t("department.config.clipboard.wizard.roles.column.workerType"),
				flex: 1.1,
				minWidth: 150,
				renderCell: ({ row }) => (
					<Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", height: "100%", minWidth: 0 }}>
						<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, lineHeight: 1.3 }} noWrap>
							{row.roleString}
						</Typography>
						<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: "text.secondary", lineHeight: 1.3 }} noWrap>
							{row.normalized}
						</Typography>
					</Box>
				),
			},
			{
				field: "disposition",
				headerName: t("department.config.clipboard.wizard.roles.column.today"),
				width: 118,
				renderCell: ({ row }) => (
					<MetaChip
						label={t(CLIPBOARD_DISPOSITION_LABEL_KEY[row.disposition])}
						tone={CLIPBOARD_DISPOSITION_TONE[row.disposition]}
						size="small"
						dataTestId={`clipboard-wizard-role-disposition-${roleSlug(row)}`}
					/>
				),
			},
			{
				field: "proposal",
				headerName: t("department.config.clipboard.wizard.roles.column.proposal"),
				flex: 1.2,
				minWidth: 170,
				valueGetter: (_value, row) => proposalText(row),
			},
			{
				field: "proposalReason",
				headerName: t("department.config.clipboard.wizard.roles.column.reason"),
				flex: 1.4,
				minWidth: 200,
				// A reason this build does not know about says so, rather than printing a raw backend token at the operator.
				valueGetter: (_value, row) => t(CLIPBOARD_PROPOSAL_REASON_KEY[row.proposalReason] ?? "department.config.clipboard.wizard.roles.reason.unknown"),
			},
		],
		[t, proposalText],
	);

	const certColumns = useMemo<SystemGridColDef<ClipboardRoleMappingRow>[]>(
		() => [
			...sharedColumns,
			{
				field: "affectedEmployeeCount",
				headerName: t("department.config.clipboard.wizard.roles.column.impact"),
				width: 200,
				sortable: false,
				renderCell: ({ row }) =>
					row.affectedEmployeeCount === 0 ? (
						<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: "text.secondary" }}>{t("department.config.clipboard.wizard.roles.impact.none")}</Typography>
					) : (
						<MetaChip
							label={t("department.config.clipboard.wizard.roles.impact.count", { count: row.affectedEmployeeCount })}
							tone="warning"
							size="small"
							onClick={() => openReview(row)}
							ariaLabel={t("department.config.clipboard.wizard.roles.impact.review", { roleString: row.roleString })}
							dataTestId={`clipboard-wizard-role-impact-${roleSlug(row)}`}
						/>
					),
			},
		],
		[sharedColumns, t, openReview],
	);

	const statusMessage = proposal && proposal.status !== "ok" ? t(CLIPBOARD_STATUS_MESSAGE_KEY[proposal.status]) : null;

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, width: "100%" }}>
			{(errorMessage ?? statusMessage) && (
				<InlineHint
					tone="warning"
					dataTestId="clipboard-wizard-roles-status"
					action={
						onRetry ? (
							<SystemButton buttonVariant="borderless" size="small" onClick={onRetry} data-testid="clipboard-wizard-roles-retry">
								{t("department.config.clipboard.wizard.roles.retry")}
							</SystemButton>
						) : undefined
					}
				>
					{errorMessage ?? statusMessage}
				</InlineHint>
			)}
			<SettingsSection
				headerOnly
				icon={<RuleOutlined />}
				title={t("department.config.clipboard.wizard.roles.group.catalog")}
				description={t("department.config.clipboard.wizard.roles.group.catalog.description")}
				dataTestId="clipboard-wizard-roles-catalog-header"
			/>
			<SystemDataGrid<ClipboardRoleMappingRow>
				rows={catalogRows}
				columns={sharedColumns}
				getRowId={(row) => row.roleString}
				loading={isLoading}
				containedHeight={GRID_CONTAINED_HEIGHT}
				emptyState={
					<EmptyState
						icon={<GroupsOutlined />}
						title={t("department.config.clipboard.wizard.roles.empty.title")}
						hint={t("department.config.clipboard.wizard.roles.empty.hint")}
						dataTestId="clipboard-wizard-roles-catalog-empty"
					/>
				}
				dataGridProps={{
					checkboxSelection: true,
					rowSelectionModel: catalogSelection,
					onRowSelectionModelChange: handleCatalogSelectionChange,
					// A row with nothing to write is shown for completeness (it is evidence the classifier
					// looked at it), but there is no action to opt into.
					isRowSelectable: ({ row }) => isProposalRow(row) && isWritable(row),
				}}
				dataTestId="clipboard-wizard-roles-catalog"
			/>
			{catalogRows.some((row) => row.proposal === "ignore") && (
				<InlineHint tone="neutral" dataTestId="clipboard-wizard-roles-ignore-note">
					{t("department.config.clipboard.wizard.roles.ignoreNote")}
				</InlineHint>
			)}
			{certRows.length > 0 && (
				<>
					<SettingsSection
						headerOnly
						icon={<GroupsOutlined />}
						title={t("department.config.clipboard.wizard.roles.group.certOverrides")}
						description={t("department.config.clipboard.wizard.roles.group.certOverrides.description")}
						dataTestId="clipboard-wizard-roles-cert-header"
					/>
					<SystemDataGrid<ClipboardRoleMappingRow>
						rows={certRows}
						columns={certColumns}
						getRowId={(row) => row.roleString}
						loading={isLoading}
						containedHeight={GRID_CONTAINED_HEIGHT}
						dataGridProps={{
							checkboxSelection: true,
							rowSelectionModel: certSelection,
							onRowSelectionModelChange: handleCertSelectionChange,
							isRowSelectable: ({ row }) => isProposalRow(row) && isWritable(row),
						}}
						dataTestId="clipboard-wizard-roles-cert"
					/>
					{reviewError && (
						<InlineHint tone="warning" dataTestId="clipboard-wizard-roles-review-error">
							{reviewError}
						</InlineHint>
					)}
				</>
			)}
			{reviewing && (
				<JobMappingChangeConfirmDialog
					open
					roleString={reviewing.row.roleString}
					currentJobLabel={jobLabel(reviewing.row.resolvedJobId) || t("hris.mapping.jobChangeConfirm.noJob")}
					newJobLabel={jobLabel(reviewing.row.targetJobId)}
					rows={reviewing.previewRows}
					onConfirm={() => {
						// Confirming IS the opt-in: the row was left unchecked precisely because it moves employees.
						// Selection state is always an `include` model (see `normalizeSelection`), so this is an add.
						setCertSelection((prev) => ({ type: "include", ids: new Set([...prev.ids, reviewing.row.roleString]) }));
						setReviewing(null);
					}}
					onClose={() => setReviewing(null)}
				/>
			)}
		</Box>
	);
};
